import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Flame, Activity, Clock } from "lucide-react";

function timeRemaining(expiresAt) {
    const t = new Date(expiresAt).getTime();
    const diff = t - Date.now();
    if (diff <= 0) return "expired";
    const d = Math.floor(diff / 86400000);
    if (d > 0) return `${d}d left`;
    const h = Math.floor(diff / 3600000);
    return `${h}h left`;
}

export default function FuelTracker() {
    const [data, setData] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/director/fuel-tracker");
            setData(data);
        } catch {
            setData({ vessels: [], treasury_balance: 0, active_count: 0, expired_count: 0 });
        }
    };

    useEffect(() => {
        load();
        const i = setInterval(load, 30000);
        return () => clearInterval(i);
    }, []);

    if (!data) return null;

    return (
        <section className="mt-12" data-testid="fuel-tracker">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <div className="font-heading text-2xl flex items-center gap-2">
                        <Flame className="text-[hsl(45_85%_68%)]" size={20} /> Fuel Tracker
                    </div>
                    <div className="text-xs text-muted-foreground">Active marketing strikes across the fleet · 30s refresh</div>
                </div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[hsl(45_85%_68%)]">
                    <Activity size={12} className="animate-pulse" />
                    {data.active_count} active
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <KPI label="Foundry Treasury" value={`$${Number(data.treasury_balance).toLocaleString()}`} accent="hsl(45 85% 68%)" />
                <KPI label="Active strikes" value={data.active_count} accent="hsl(40 70% 55%)" />
                <KPI label="Expired" value={data.expired_count} accent="hsl(151 30% 60%)" />
            </div>

            {data.vessels.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center text-muted-foreground text-sm">
                    No sites currently receiving fuel. Once a Commander deploys a payload, the delivery will land here.
                </div>
            ) : (
                <div className="glass rounded-2xl p-2 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-[hsl(40_40%_30%/0.18)]">
                            <tr>
                                <th className="py-3 px-4">Vessel</th>
                                <th className="py-3 px-4">Active strikes</th>
                                <th className="py-3 px-4">Burning</th>
                                <th className="py-3 px-4">Commanders</th>
                                <th className="py-3 px-4">Latest</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.vessels.map((v) => (
                                <tr key={v.state_code} className="border-b border-[hsl(40_40%_30%/0.08)] hover:bg-[hsl(151_55%_13%)]" data-testid={`fuel-vessel-${v.state_code}`}>
                                    <td className="py-3 px-4 font-mono">
                                        <div className="inline-flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-[hsl(45_85%_62%)] animate-pulse" />
                                            {v.state_code}
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 font-mono">{v.strikes}</td>
                                    <td className="py-3 px-4 font-mono text-[hsl(45_85%_68%)]">${Number(v.total_amount).toLocaleString()}</td>
                                    <td className="py-3 px-4 font-mono">{v.partner_count}</td>
                                    <td className="py-3 px-4 text-xs">
                                        {v.recent[0] && (
                                            <div className="space-y-0.5">
                                                <div className="text-foreground">{v.recent[0].agency}</div>
                                                <div className="text-muted-foreground inline-flex items-center gap-1">
                                                    <Clock size={10} />
                                                    {timeRemaining(v.recent[0].expires_at)}
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

function KPI({ label, value, accent }) {
    return (
        <div className="glass rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            <div className="font-heading text-3xl mt-1" style={{ color: accent }}>{value}</div>
        </div>
    );
}
